import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid2,
	Snackbar,
	SnackbarCloseReason,
	TextField,
	useTheme,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import * as React from "react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { ErrorLabel } from "../../../../../controls/error-label";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { User } from "../../models/User";
import {
	setPassword,
	setProfilesToUser,
	upsertUser,
} from "../../repositories/UsersRepository";
import { AssetSelector } from "../common/AssetSelector";
import { AuthTypeSelector } from "../common/AuthTypeSelector";
import { ProfilesPicker } from "../common/ProfilesPicker";

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
};

export const CreateUser = ({ show, onHide, suffixTitle }: Props) => {
	const theme = useTheme();

	const [open, setOpen] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [profileError, setprofileError] = useState("");

	const {
		register,
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm<User>({ defaultValues: INITIAL_VALUES });

	const handleClose = (
		event?: React.SyntheticEvent | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	const UserName = watch("UserName");

	const createUser = useMutation({
		mutationFn: async (data: User) => {
			// Step 1: Create/update the user
			const userId = await upsertUser(data);

			// Step 2: Set the initial password (username as default password)
			const passwordResult = await setPassword(userId, data.UserName);
			if (!passwordResult.ok) {
				throw new Error(passwordResult.message || "Error setting password");
			}

			// Step 3: Set the profiles for the user
			if (data.ProfilesId && Array.isArray(data.ProfilesId)) {
				await setProfilesToUser({
					UserId: userId,
					profileIds: data.ProfilesId,
				});
			}

			return userId;
		},
		onSuccess: () => {
			setOpen(true);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const onSubmit: SubmitHandler<User> = async (data: any) => {
		setIsSubmitLoading(true);
		await createUser.mutate(data);
	};

	useEffect(() => {
		if (show) {
			reset();
		}
	}, [show]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`New User${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<MesfModal
				title="NEW USER"
				open={show}
				handleClose={() => onHide(false)}
				id="create-user-modal"
				maxWidth="md"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content>
						<Grid2 container spacing={1} alignItems="center">
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									{...register("UserName", { required: true })}
									label="Username"
									variant="outlined"
									error={!!errors.UserName}
									helperText={
										errors.UserName?.type === "required"
											? "User name is required"
											: ""
									}
									fullWidth
									margin="dense"
									autoComplete="off"
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									disabled
									label="Password"
									value={UserName}
									variant="outlined"
									error={!!errors.UserName}
									fullWidth
									margin="dense"
									autoComplete="off"
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 12 }}>
								<TextField
									{...register("FirstName", { required: true })}
									label="First name"
									variant="outlined"
									error={!!errors.FirstName}
									helperText={
										errors.FirstName?.type === "required"
											? "First name is required"
											: ""
									}
									fullWidth
									margin="dense"
									autoComplete="off"
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 12 }}>
								<TextField
									{...register("LastName", { required: true })}
									label="Last name"
									variant="outlined"
									error={!!errors.FirstName}
									helperText={
										errors.LastName?.type === "required"
											? "Last name is required"
											: ""
									}
									fullWidth
									margin="dense"
									autoComplete="off"
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Controller
									name="ProfilesId"
									control={control}
									rules={{ required: true }}
									render={({ field: { onChange, value } }) => (
										<ProfilesPicker
											onChange={onChange}
											value={value as number[]}
											userIdForDelete={null}
											setprofileError={setprofileError}
										/>
									)}
								/>
								{errors.ProfilesId && <ErrorLabel text="Select a profile(s)" />}
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Controller
									name="AuthTypeId"
									control={control}
									render={({ field: { value, onChange } }) => (
										<AuthTypeSelector value={value} onChange={onChange} />
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Controller
									name="DefaultAssetId"
									control={control}
									render={({ field: { value, onChange } }) => (
										<AssetSelector value={value} onChange={onChange} />
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }} style={{ marginTop: "5px" }}>
								<Controller
									name="IsActive"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											sx={{
												userSelect: "none",
											}}
											control={
												<Checkbox
													checked={field.value}
													onChange={field.onChange}
													name="IsActive"
													color="primary"
												/>
											}
											label="Active"
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
				<ErrorModal
					error={error}
					onHide={() => setError("")}
					title="Error Creating User"
				/>
				<ErrorModal
					error={profileError}
					onHide={() => setprofileError("")}
					title="Error Choosing Profile"
				/>
			</MesfModal>
			<Snackbar
				open={open}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={handleClose}>
					The user was created successfully
				</Alert>
			</Snackbar>
		</>
	);
};
